import type { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { ContractOtherCostLimitSpanSelectionItem } from 'gql/graphql';
import type { GroupCollapseState } from 'pages/Project/UerProject/ActKC3List/components/ActKC3ListTable/types';
import { uerContractOtherCostLimitSpanSelectionListGql } from 'pages/Project/UerProject/Contract/ContractQueries';

import { useCallback, useEffect, useMemo, useState } from 'react';

interface IUseUerContractOtherCostLimitSpanSelectionList {
    contractId: number;
    otherCostId?: number;
    withEffect?: boolean;
    constructionTypeId?: number;
}

export interface IContractOtherCostLimitSpanSelectionItem extends ContractOtherCostLimitSpanSelectionItem {
    id: string;
    groupKey?: string;
    isGroup?: boolean;
    isGroupMember?: boolean;
    spanMember?: boolean;
    isTotal?: boolean;
    contractSeasonId?: number;
}

interface IUerContractOtherCostLimitSpanSelectionGroupedData {
    items: Map<string, IContractOtherCostLimitSpanSelectionItem[]>;
    initialCollapseState: Record<string, boolean>;
}

export interface IUerContractOtherCostLimitSpanSelectionData {
    data: IContractOtherCostLimitSpanSelectionItem[];
    status: RequestStatus;
    allIds: string[];
    allCostFieldsIds: string[];
    reminders: number[];
    totalsRow: any;
    changeCollapseState: (groupKey: string) => () => void;
    collapseState: GroupCollapseState;
}

const requestId = 'uerContractOtherCostLimitSpanSelectionList';

export const useUerContractOtherCostLimitSpanSelectionList = (
    props: IUseUerContractOtherCostLimitSpanSelectionList,
): IUerContractOtherCostLimitSpanSelectionData => {
    const { contractId, otherCostId, withEffect, constructionTypeId } = props;

    const variables = useMemo(
        () => ({
            contractId,
            otherCostId,
        }),
        [contractId, otherCostId],
    );

    const [uerContractOtherCostLimitSpanSelectionListData, uerContractOtherCostLimitSpanSelectionListStatus] =
        useRequest({
            requestId,
            query: uerContractOtherCostLimitSpanSelectionListGql,
            withEffect: !!withEffect && Boolean(contractId),
            variables,
        });

    const groupedListData = useMemo(() => {
        const accInit: IUerContractOtherCostLimitSpanSelectionGroupedData = {
            items: new Map(),
            initialCollapseState: {},
        };

        if (!uerContractOtherCostLimitSpanSelectionListData?.uerContractOtherCostLimitSpanSelectionList?.length) {
            return {
                itemsWithGroups: [],
                initialCollapseState: {},
            };
        }

        const groupedData =
            uerContractOtherCostLimitSpanSelectionListData?.uerContractOtherCostLimitSpanSelectionList?.reduce(
                (acc, item, currentIndex) => {
                    const id = String(currentIndex + 1);
                    const topLevelItem = {
                        ...item,
                        id,
                        isGroup: !!item.spanSeasons?.length,
                        spanMember: !item.spanSeasons?.length,
                        groupKey: id,
                    };

                    const subLevelItems: any[] =
                        item.spanSeasons?.map((s) => ({
                            id: `${id}.${s.year}`,
                            groupKey: id,
                            isGroup: false,
                            isGroupMember: true,
                            spanMember: true,
                            name: `Сезон ${s.year}`,
                            plan: s.plan,
                            actual: s.actual,
                            reminder: s.reminder,
                            cost: s.cost,
                            constructionTypeId: item.constructionTypeId,
                            contractSeasonId: s.contractSeasonId,
                            isSelected: true,
                        })) || [];

                    acc.items.set(id, [topLevelItem, ...subLevelItems]);
                    acc.initialCollapseState[id] = false;

                    return acc;
                },
                accInit,
            );

        const itemsWithGroups = Array.from(groupedData.items.values()).flat();

        return {
            itemsWithGroups,
            initialCollapseState: groupedData.initialCollapseState,
        };
    }, [uerContractOtherCostLimitSpanSelectionListData?.uerContractOtherCostLimitSpanSelectionList]);

    const [collapseState, setCollapseState] = useState<GroupCollapseState>({});

    const allIds = useMemo(() => groupedListData.itemsWithGroups.map((i) => i.id), [groupedListData.itemsWithGroups]);

    const allCostFieldsIds = useMemo(() => allIds.map((id) => `costLimitSpan_${id}`), [allIds]);

    const reminders = useMemo(
        () => groupedListData.itemsWithGroups.map((i) => i.reminder || 0),
        [groupedListData.itemsWithGroups],
    );

    const totalsRow = useMemo(() => {
        const row = {
            id: groupedListData.itemsWithGroups.length + 1,
            isTotal: true,
            name: 'Итого',
            plan: 0,
            actual: 0,
            reminder: 0,
        };

        const filteredData = groupedListData.itemsWithGroups.filter(
            (item) => item.constructionTypeId === constructionTypeId,
        );

        return filteredData.reduce((acc, item) => {
            row.plan += item.plan || 0;
            row.actual += item.actual || 0;
            row.reminder += item.reminder || 0;

            return row;
        }, row);
    }, [constructionTypeId, groupedListData.itemsWithGroups]);

    const actualGroupedItems = useMemo(
        () =>
            groupedListData?.itemsWithGroups.filter(
                (item) => item.groupKey && (item.isGroup || !collapseState[item.groupKey]),
            ),
        [collapseState, groupedListData?.itemsWithGroups],
    );

    const [uerContractOtherCostLimitSpanSelectionListReset] = useResetStoreData({ requestId });

    useEffect(() => {
        uerContractOtherCostLimitSpanSelectionListReset(undefined);
    }, [uerContractOtherCostLimitSpanSelectionListReset]);

    useEffect(() => {
        if (groupedListData?.initialCollapseState) {
            setCollapseState((prevState) => ({ ...groupedListData?.initialCollapseState, ...prevState }));
        }
    }, [groupedListData?.initialCollapseState]);

    const changeCollapseState = useCallback(
        (groupKey: string) => () => {
            setCollapseState((prevState) => ({ ...prevState, [groupKey]: !prevState[groupKey] }));
        },
        [],
    );

    return {
        data: actualGroupedItems,
        status: uerContractOtherCostLimitSpanSelectionListStatus,
        allIds,
        allCostFieldsIds,
        reminders,
        totalsRow,
        changeCollapseState,
        collapseState,
    };
};
